import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGetCustomerByID } from '../../../../../api/dealership/customers/useGetCustomerByID'
import dayjs from 'dayjs'
import classNames from 'classnames'
import { formatNumber } from '../../../../../lib/utils/formatNumber'
import {
  Car,
  Phone,
  UserPlus,
  MapPin,
  Mail,
  UserCog,
  Ban,
  CalendarClock,
  ShoppingCart,
  FileSpreadsheet,
  Users,
  UserCheck,
  Merge,
  Fingerprint,
  Eye,
  Edit,
  MousePointerClick,
  Globe,
} from 'lucide-react'
import { isEmpty, isObject } from 'lodash'
import type { Customer } from '@demandlocal/utils/lib/mongo/models/Customers'
import { LoadingComponent, Pill } from '@demandlocal/components'
import { match } from 'ts-pattern'
import { ParsedClickHouseCustomerEvent } from '../../../../../types/CustomerEvent'
import extractURLParams from '../../../../../lib/utils/extractURLParams'

const EmptyState = ({ type }: { type: 'customer-journey' | 'data-journey' }) => {
  const getEmptyStateContent = () => {
    switch (type) {
      case 'customer-journey':
        return {
          icon: <ShoppingCart className="h-10 w-10 text-gray-400" />,
          title: 'No Customer Journey Events',
          description:
            'No sales, leads, or service interactions found for this customer.',
        }
      case 'data-journey':
        return {
          icon: <FileSpreadsheet className="h-10 w-10 text-gray-400" />,
          title: 'No Data Journey Events',
          description: 'No data updates or system events found for this customer.',
        }
    }
  }

  const content = getEmptyStateContent()

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-lg border border-gray-200 shadow-sm">
      {content.icon}
      <h3 className="mt-3 text-base font-semibold text-gray-900">{content.title}</h3>
      <p className="mt-1 text-sm text-gray-500">{content.description}</p>
    </div>
  )
}

const TimelineConnector = () => (
  <div className="absolute top-14 bottom-0 left-6 w-px bg-gainsboro-gray -z-10" />
)

const getEventIcon = (eventType: ParsedClickHouseCustomerEvent<any>['event_type']) => {
  const icons = {
    customer_created: UserCheck,
    customer_updated: UserCog,
    customer_merged: Merge,
    vehicle_service: Car,
    sale_created: ShoppingCart,
    lead_created: FileSpreadsheet,
    uuid_created: Fingerprint,
    vehicle_added: Car,
    vehicle_removed: Car,
    co_buyer_added: UserPlus,
    service_created: CalendarClock,
    marketing_opt_out: Ban,
    phone_call: Phone,
    address_updated: MapPin,
    phone_updated: Phone,
    email_updated: Mail,
    audience_customer_added: Users,
    audience_customer_removed: Users,
    impression_created: Eye,
    click_created: MousePointerClick,
    site_visit_created: Globe,
  }

  const IconComponent = icons[eventType] || Edit
  return <IconComponent className="h-5 w-5" />
}

const getEventColor = (eventType: ParsedClickHouseCustomerEvent<any>['event_type']) => {
  const colors = {
    customer_created: 'text-green-600',
    customer_updated: 'text-blue-600',
    customer_merged: 'text-purple-600',
    vehicle_service: 'text-indigo-600',
    sale_created: 'text-emerald-600',
    lead_created: 'text-cyan-600',
    uuid_created: 'text-slate-600',
    vehicle_added: 'text-indigo-600',
    vehicle_removed: 'text-red-600',
    co_buyer_added: 'text-teal-600',
    service_created: 'text-violet-600',
    marketing_opt_out: 'text-orange-600',
    phone_call: 'text-blue-600',
    address_updated: 'text-amber-600',
    phone_updated: 'text-blue-600',
    email_updated: 'text-cyan-600',
    audience_customer_added: 'text-green-600',
    audience_customer_removed: 'text-red-600',
  }

  return colors[eventType] || 'text-gray-600'
}

const EventDetails = ({ event }: { event: ParsedClickHouseCustomerEvent<any> }) => {
  const renderVehicleInfo = (vehicle) => {
    if (!vehicle) return null

    return (
      <div className="mt-4 grid grid-cols-2 gap-6 border-t border-gray-100 pt-4">
        <div>
          <h4 className="text-gray-500 font-medium text-sm mb-1">Vehicle</h4>
          <p className="text-gray-900">
            {vehicle.make} {vehicle.model}
          </p>
        </div>
        <div>
          <h4 className="text-gray-500 font-medium text-sm mb-1">Year</h4>
          <p className="text-gray-900">{vehicle.year}</p>
        </div>
        <div className="col-span-2">
          <h4 className="text-gray-500 font-medium text-sm mb-1">VIN</h4>
          <p className="text-gray-900 font-mono">{vehicle.vin}</p>
        </div>
        {vehicle.mileage && (
          <div>
            <h4 className="text-gray-500 font-medium text-sm mb-1">Mileage</h4>
            <p className="text-gray-900">{formatNumber(vehicle.mileage)} miles</p>
          </div>
        )}
      </div>
    )
  }

  const renderAddressInfo = (address) => {
    const addressData = isObject(address?.address) ? address.address : address
    return (
      <div className="mt-4 border-t border-gray-100 pt-4">
        <div className="space-y-4">
          <div>
            <h4 className="text-gray-500 font-medium text-sm mb-1">Address</h4>
            <p className="text-gray-900">{addressData?.address?.toString() || ''}</p>
          </div>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <h4 className="text-gray-500 font-medium text-sm mb-1">City</h4>
              <p className="text-gray-900">{addressData?.city?.toString() || ''}</p>
            </div>
            <div>
              <h4 className="text-gray-500 font-medium text-sm mb-1">State</h4>
              <p className="text-gray-900">{addressData?.state?.toString() || ''}</p>
            </div>
            <div>
              <h4 className="text-gray-500 font-medium text-sm mb-1">ZIP</h4>
              <p className="text-gray-900">{addressData?.zip?.toString() || ''}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderEmailInfo = (email) => {
    const emailData = isObject(email?.email) ? email.email : email
    return (
      <div className="mt-4 border-t border-gray-100 pt-4">
        <div className="space-y-3">
          <div>
            <h4 className="text-gray-500 font-medium text-sm mb-1">Email Address</h4>
            {/* Add a null check and ensure we're rendering a string */}
            <p className="text-gray-900">{emailData?.email?.toString() || ''}</p>
          </div>
          <div className="flex gap-3">
            {emailData?.preferred && <Pill type="success">Preferred</Pill>}
            {emailData?.do_not_email && <Pill type="error">Do Not Email</Pill>}
          </div>
        </div>
      </div>
    )
  }

  const renderPhoneInfo = (phone) => {
    const phoneData = isObject(phone?.phone) ? phone.phone : phone
    return (
      <div className="mt-4 border-t border-gray-100 pt-4">
        <div className="space-y-3">
          <div>
            <h4 className="text-gray-500 font-medium text-sm mb-1">Phone Number</h4>
            <p className="text-gray-900">{phoneData.local}</p>
          </div>
          <div className="flex gap-3">
            {phoneData.preferred && <Pill type="success">Preferred</Pill>}
            {phoneData.do_not_call && <Pill type="error">Do Not Call</Pill>}
            {phoneData.type && <Pill type="info-purple">{phoneData.type}</Pill>}
          </div>
        </div>
      </div>
    )
  }

  const renderAudienceInfo = (audienceData) => {
    if (!audienceData.audience) return null

    const audience = audienceData.audience
    return (
      <div className="mt-4 border-t border-gray-100 pt-4">
        <div className="space-y-4">
          <div>
            <h4 className="text-gray-500 font-medium text-sm mb-1">Audience Name</h4>
            <p className="text-gray-900">
              {audience.audience_name || 'Unnamed Audience'}
            </p>
          </div>
          {audience.description && (
            <div>
              <h4 className="text-gray-500 font-medium text-sm mb-1">Description</h4>
              <p className="text-gray-900">{audience.description}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Pill
              type={
                match(audience.audience_type)
                  .with('custom', () => 'gray')
                  .with('sales', () => 'success')
                  .with('service', () => 'info-purple')
                  .with('leads', () => 'warning')
                  .otherwise(() => 'error') as any
              }>
              {audience.audience_type}
            </Pill>
            <Pill type="gray">
              <Users className="w-3 h-3 mr-1" />
              {audience.audience_size?.toLocaleString()} members
            </Pill>
            {audience.created_by && (
              <Pill type="gray">
                Created by {audience.created_by.first_name}{' '}
                {audience.created_by.last_name}
              </Pill>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderCampaignInfo = (campaign) => {
    if (!campaign) return null

    const { campaign_data } = campaign

    return (
      <div className="mt-4 grid grid-cols-2 gap-6 border-t border-gray-100 pt-4">
        {!isEmpty(campaign_data) && (
          <>
            <div>
              <h4 className="text-gray-500 font-medium text-sm mb-1">Campaign</h4>
              <p className="text-gray-900">
                {campaign_data.client_campaign_display_name}
              </p>
            </div>
            <div>
              <h4 className="text-gray-500 font-medium text-sm mb-1">Product</h4>
              <p className="text-gray-900">{campaign_data.product_name}</p>
            </div>
            <div>
              <h4 className="text-gray-500 font-medium text-sm mb-1">Creative Name</h4>
              <p className="text-gray-900 font-mono">
                {campaign_data.vendor_creative_name}
              </p>
            </div>
            <div>
              <h4 className="text-gray-500 font-medium text-sm mb-1">Creative Size</h4>
              <p className="text-gray-900 font-mono">
                {campaign_data.vendor_creative_size}
              </p>
            </div>
          </>
        )}
        <div>
          <h4 className="text-gray-500 font-medium text-sm mb-1">Location</h4>
          <p className="text-gray-900">
            {campaign.city}, {campaign.region}
          </p>
        </div>
        <div>
          <h4 className="text-gray-500 font-medium text-sm mb-1">Publisher</h4>
          <p className="text-gray-900">{campaign.supplyvendorname}</p>
        </div>
        <div>
          <h4 className="text-gray-500 font-medium text-sm mb-1">Device</h4>
          <p className="text-gray-900">
            {campaign.devicetypename}{' '}
            {campaign.devicemodel ? `(${campaign.devicemodel})` : ''}
          </p>
        </div>
        <div>
          <h4 className="text-gray-500 font-medium text-sm mb-1">Device Make</h4>
          <p className="text-gray-900">{campaign.devicemake}</p>
        </div>
      </div>
    )
  }

  const renderSiteVisitInfo = (data) => {
    if (!data) return null
    const { referrerurl } = data
    const queryParams = extractURLParams(referrerurl, {
      queryParam: 'utm_',
    })
    // Function to format the parameter key (e.g., "utm_source" -> "UTM Source")
    const formatParameterKey = (key) => {
      return key
        .split('_')
        .map((word: string) => {
          // If the word is "utm", capitalize the entire word
          if (word.toLowerCase() === 'utm') return 'UTM'
          // Otherwise, capitalize just the first letter
          return word.charAt(0).toUpperCase() + word.slice(1)
        })
        .join(' ')
    }

    return (
      <div className="mt-4 grid grid-cols-2 gap-6 border-t border-gray-100 pt-4">
        <div>
          <h4 className="text-gray-500 font-medium text-sm mb-1">Tracking Tag</h4>
          <p className="text-gray-900">{data?.tag_name}</p>
        </div>
        <div>
          <h4 className="text-gray-500 font-medium text-sm mb-1">Referrer URL</h4>
          <p className="text-gray-900">{referrerurl}</p>
        </div>
        {Object.entries(queryParams).map(([key, value]) => (
          <div key={key}>
            <h4 className="text-gray-500 font-medium text-sm mb-1">
              {formatParameterKey(key)}
            </h4>
            <p className="text-gray-900 font-mono">{value}</p>
          </div>
        ))}
      </div>
    )
  }

  switch (event.event_type) {
    case 'lead_created':
    case 'sale_created':
    case 'vehicle_service':
    case 'vehicle_added':
    case 'vehicle_removed':
      return renderVehicleInfo(
        (event.event_data as unknown as any)?.vehicle || event.event_data,
      )

    case 'address_updated':
      return renderAddressInfo(event.event_data)

    case 'email_updated':
      return renderEmailInfo(event.event_data)

    case 'phone_updated':
      return renderPhoneInfo(event.event_data)

    case 'audience_customer_added':
    case 'audience_customer_removed':
      return renderAudienceInfo(event.event_data)

    case 'impression_created':
    case 'click_created':
      return renderCampaignInfo(event.event_data)

    case 'site_visit_created':
      return renderSiteVisitInfo(event.event_data)

    case 'co_buyer_added':
      const coBuyer = event.event_data as Customer
      return (
        <div className="mt-4 bg-gray-50 p-4 rounded-lg">
          <div className="mb-3">
            <h4 className="text-gray-500 font-medium text-sm">Co-buyer Name</h4>
            <p className="text-gray-900">
              {coBuyer?.first_name ?? ''} {coBuyer?.last_name ?? ''}
            </p>
          </div>
          {coBuyer?._id && (
            <a
              href={`./../${coBuyer._id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium">
              <UserPlus className="h-4 w-4 mr-2" />
              View Profile
            </a>
          )}
        </div>
      )

    case 'phone_call':
      const callData = event.event_data.action_details as {
        created_date?: string
        phone?: string
        phone_call_vendor_id?: string
      }
      return (
        <div className="mt-4 p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-gray-500 font-medium text-sm">Phone Number</h4>
              <p className="text-gray-900">{callData?.phone}</p>
            </div>
            <div>
              <h4 className="text-gray-500 font-medium text-sm">Vendor</h4>
              <p className="text-gray-900">{callData?.phone_call_vendor_id}</p>
            </div>
          </div>
        </div>
      )

    default:
      if (Object.keys(event.event_data || {}).length > 0) {
        return (
          <div className="mt-4 bg-gray-50 p-4 rounded-lg">
            <div className="space-y-2">
              <h4 className="text-gray-500 font-medium text-sm">Event Details</h4>
              <pre className="text-xs overflow-auto bg-white p-2 rounded border border-gray-200">
                {JSON.stringify(event.event_data, null, 2)}
              </pre>
            </div>
          </div>
        )
      }
      return null
  }
}

const getEventTitle = (eventType: ParsedClickHouseCustomerEvent<any>['event_type']) => {
  const titles = {
    customer_created: 'Customer Created',
    customer_updated: 'Customer Updated',
    customer_merged: 'Customer Merged',
    vehicle_service: 'Vehicle Service',
    sale_created: 'Vehicle Purchase',
    lead_created: 'Lead Created',
    uuid_created: 'UUID Created',
    vehicle_added: 'Vehicle Added',
    vehicle_removed: 'Vehicle Removed',
    co_buyer_added: 'Co-buyer Added',
    service_created: 'Service Scheduled',
    marketing_opt_out: 'Marketing Opt-out',
    phone_call: 'Phone Call',
    address_updated: 'Address Updated',
    phone_updated: 'Phone Updated',
    email_updated: 'Email Updated',
    audience_customer_added: 'Added to Audience',
    audience_customer_removed: 'Removed from Audience',
    impression_created: 'Ad Impression',
    click_created: 'Ad Click',
    site_visit_created: 'Influenced Site Visit',
  }

  return titles[eventType] || 'Unknown Event'
}

const getEventCategory = (eventType) => {
  const journeyEvents = [
    'lead_created',
    'sale_created',
    'service_created',
    'phone_call',
    'co_buyer_added',
    'service_created',
    'impression_created',
    'click_created',
    'site_visit_created',
  ]

  return journeyEvents.includes(eventType) ? 'customer-journey' : 'data-journey'
}

export default function CustomerEvents() {
  const { customer_id } = useParams()
  const { data: customer } = useGetCustomerByID(customer_id)
  const [timelineType, setTimelineType] = useState<'customer-journey' | 'data-journey'>(
    'customer-journey',
  )

  const timeline = useMemo(() => {
    if (!customer?.events) return []

    return customer.events
      .map((event) => ({
        id: event.id,
        title: getEventTitle(event.event_type),
        time: dayjs(event.event_date).format('MMM D, YYYY h:mm A'),
        timestamp: new Date(event.event_date).getTime(),
        details: <EventDetails event={event} />,
        category: getEventCategory(event.event_type),
        originalEvent: event,
        icon: getEventIcon(event.event_type),
        colorClass: getEventColor(event.event_type),
      }))
      .sort((a, b) => b.timestamp - a.timestamp)
  }, [customer?.events])

  if (!customer) return <LoadingComponent title={'Loading customer'} />

  const hasEvents = (customer?.events?.length || 0) > 0
  const filteredEvents = timeline.filter((item) => item.category === timelineType)

  return (
    <div className="flex flex-1 flex-col h-full">
      {/* Sticky header with tabs - keeping the clean design */}
      <div className="sticky top-0 bg-white z-10">
        <div className="px-4 py-2">
          <ul className="flex flex-wrap text-sm font-medium text-center gap-2">
            <li
              onClick={() => setTimelineType('customer-journey')}
              className={classNames(
                'px-4 py-2 rounded-lg cursor-pointer transition-colors duration-200',
                {
                  'bg-primary-persian-blue text-white':
                    timelineType === 'customer-journey',
                  'text-web-gray hover:bg-gray-100': timelineType !== 'customer-journey',
                },
              )}>
              Customer Journey
            </li>
            <li
              onClick={() => setTimelineType('data-journey')}
              className={classNames(
                'px-4 py-2 rounded-lg cursor-pointer transition-colors duration-200',
                {
                  'bg-primary-persian-blue text-white': timelineType === 'data-journey',
                  'text-web-gray hover:bg-gray-100': timelineType !== 'data-journey',
                },
              )}>
              Data Journey
            </li>
          </ul>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-4">
          {!hasEvents ? (
            <EmptyState type={timelineType} />
          ) : filteredEvents.length === 0 ? (
            <EmptyState type={timelineType} />
          ) : (
            filteredEvents.map((item) => (
              <div
                key={`${item.timestamp}_${item.originalEvent.event_type}_${item.originalEvent?.deduplication_key}`}
                className={classNames(
                  'rounded-lg border border-[3px] bg-opacity-10 border-gray-200 shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-md',
                  {
                    'border-secondary-malachite-green bg-secondary-malachite-green font-bold':
                      item.originalEvent?.event_type === 'impression_created',
                    'border-sunglow bg-sunglow font-bold':
                      item.originalEvent?.event_type === 'click_created',
                    'border-[#0DBDE3] bg-[#0DBDE3] font-bold':
                      item.originalEvent?.event_type === 'site_visit_created',
                  },
                )}>
                <div className="p-4">
                  {/* Header with icon and timestamp */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={classNames(
                          'p-2 rounded-full bg-gray-50',
                          item.colorClass,
                        )}>
                        {item.icon}
                      </div>
                      <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    </div>
                    <span className="text-sm text-gray-500">{item.time}</span>
                  </div>

                  {/* Event Details - keeping all the existing detailed renderers */}
                  {item.details}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
